import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useS3Uploader } from './../useS3Uploader';
import {
  getGroupbyId,
  getOrganisationActivityDetails,
  getOrganisationDetails,
  getRealmRoles,
  getRealmUserInfo,
  getRoles
} from '../../services';
import { getTimestamps } from '../../helper';
import { ROLES } from '../../constants';
import { useSelector } from 'react-redux';
import { selectOrganisationActivityRange } from 'data';
import {
  mapStaffData,
  getSignedImage,
  resolveUserRole,
  MappedStaff
} from 'data/orgnisation-management/helper';

export const useOrganisationDetails = (organisationId: string) => {
  const { generateSignedUrl } = useS3Uploader();
  const organisationAcitivityDateRange = useSelector(selectOrganisationActivityRange);

  const userAccessToken = localStorage.getItem('user_access_token') || '';
  const userRole = localStorage.getItem('userRole') || '';

  /* -------------------- TIMESTAMPS -------------------- */

  const { startTimestamp, endTimestamp } = useMemo(() => {
    if (!organisationAcitivityDateRange) {
      return { startTimestamp: '', endTimestamp: '' };
    }
    return (
      getTimestamps(organisationAcitivityDateRange) ?? {
        startTimestamp: '',
        endTimestamp: ''
      }
    );
  }, [organisationAcitivityDateRange]);

  /* -------------------- ROLES -------------------- */

  const rolesQuery = useQuery({
    queryKey: ['organisationRoles', userAccessToken, userRole],
    queryFn: async () => {
      const roles = await getRealmRoles(userAccessToken);

      const allowedRoles = [ROLES.PRACTISE_ADMIN, ROLES.DOCTOR, ROLES.DEPT_ADMIN, ROLES.OTHER];

      let filtered = roles?.filter((r: { name: string }) => allowedRoles.includes(r.name)) ?? [];

      if (userRole === ROLES.PRACTISE_ADMIN) {
        filtered = filtered.filter((r: { name: string }) => r.name !== ROLES.PRACTISE_ADMIN);
      }

      return [
        { label: 'All', value: 'All' },
        ...filtered.map((r) => ({
          label: r.name,
          value: r.name
        }))
      ];
    },
    enabled: !!userAccessToken,
    placeholderData: keepPreviousData
  });

  /* -------------------- ORGANISATION DETAILS -------------------- */
  const organisationDetailsQuery = useQuery({
    queryKey: ['organisationDetails', organisationId],
    enabled: !!organisationId && !!userAccessToken,
    placeholderData: keepPreviousData,

    queryFn: async () => {
      // ---------- BASE DATA (ONLY ORG IS REQUIRED) ----------
      const result = await getOrganisationDetails(organisationId);
      const organisation = result.practiseOrganisations?.[0];

      if (!organisation) {
        throw new Error('Invalid organisation data');
      }

      const adminDetails = organisation.practiseAdminDetails?.[0];

      /* ===================== OPTIONAL DATA ===================== */

      let orgGroup = null;
      try {
        orgGroup = await getGroupbyId(organisation.id, userAccessToken);
      } catch (e) {
        console.error('Org Group failed', e);
      }

      let deptGroup = null;
      try {
        if (adminDetails?.departmentId) {
          deptGroup = await getGroupbyId(adminDetails.departmentId, userAccessToken);
        }
      } catch (e) {
        console.error('Dept Group failed', e);
      }

      let doctorUser = null;
      try {
        if (adminDetails?.practiseUserId) {
          doctorUser = await getRealmUserInfo(adminDetails.practiseUserId, userAccessToken);
        }
      } catch (e) {
        console.error('Doctor User failed', e);
      }

      let physicians: MappedStaff[] = [];
      try {
        physicians = await mapStaffData(
          organisation.physicianData,
          userAccessToken,
          generateSignedUrl
        );
      } catch (e) {
        console.error('Physicians failed', e);
      }

      let departmentAdmins: MappedStaff[] = [];
      try {
        departmentAdmins = await mapStaffData(
          organisation.departmentAdminData,
          userAccessToken,
          generateSignedUrl
        );
      } catch (e) {
        console.error('Department Admins failed', e);
      }

      let otherStaff: MappedStaff[] = [];
      try {
        otherStaff = await mapStaffData(
          organisation.otherStaffData,
          userAccessToken,
          generateSignedUrl
        );
      } catch (e) {
        console.error('Other Staff failed', e);
      }

      let adminImage;
      try {
        if (adminDetails?.practiseUserRelation?.practiseAdminLivePic?.imageContent) {
          adminImage = await getSignedImage(
            adminDetails.practiseUserRelation.practiseAdminLivePic.imageContent,
            generateSignedUrl
          );
        }
      } catch (e) {
        console.error('Admin Image failed', e);
      }

      let roles: string[] = [];
      try {
        if (adminDetails?.practiseUserId) {
          roles = (await getRoles(adminDetails.practiseUserId, userAccessToken)) as string[];
        }
      } catch (e) {
        console.error('Roles failed', e);
      }

      let logoUrl;
      try {
        if (organisation.logoUrl) {
          logoUrl = await generateSignedUrl(organisation.logoUrl);
        }
      } catch (e) {
        console.error('Logo failed', e);
      }

      let departments = null;
      try {
        departments = await getGroupbyId(organisationId, userAccessToken);
      } catch (e) {
        console.error('Departments failed', e);
      }

      /* ===================== FINAL RETURN ===================== */

      return {
        practiseAdminDetails: adminDetails ?? null,

        hospital: orgGroup?.name ?? organisation.name ?? 'N/A',
        department: deptGroup?.name ?? 'N/A',

        image: adminImage,
        logoUrl,

        role: roles.length ? resolveUserRole(roles) : 'N/A',

        doctorName: doctorUser
          ? `${doctorUser.firstName ?? ''} ${doctorUser.lastName ?? ''}`.trim()
          : 'N/A',

        doctorsCount: organisation.doctorsCount?.aggregate?.count ?? 0,
        physicianCount: organisation.physicianCount?.aggregate?.count ?? 0,
        departmentAdminCount: departments?.subGroupCount ?? 0,
        otherStaffCount: organisation.otherStaffCount?.aggregate?.count ?? 0,

        appointmentsCount: result.appointmentsCount?.aggregate?.count ?? 0,
        totalFeedbackCount: result.totalFeedbackCount?.aggregate?.count ?? 0,
        totalPrescriptionsCount: result.totalPrescriptionsCount?.aggregate?.count ?? 0,
        patientsCount: result.patientsCount?.aggregate?.count ?? 0,

        malePatientCount:
          result.malePatientCount?.[0]?.Appointments_aggregate?.aggregate?.count ?? 0,
        femalePatientCount:
          result.femalePatientCount?.[0]?.Appointments_aggregate?.aggregate?.count ?? 0,
        otherPatientCount:
          result.otherPatientCount?.[0]?.Appointments_aggregate?.aggregate?.count ?? 0,

        physicians,
        departmentAdmins,
        otherStaff
      };
    }
  });

  const organisationName =
    organisationDetailsQuery.data?.hospital ?? organisationDetailsQuery?.data?.hospital;

  console.log(organisationName, 'organisationDetailsQuery', organisationDetailsQuery);

  /* -------------------- ORGANISATION ACTIVITY -------------------- */

  const organisationActivityQuery = useQuery({
    queryKey: ['organisationActivity', organisationId, startTimestamp, endTimestamp],
    queryFn: () =>
      getOrganisationActivityDetails({
        organisationId,
        startTimeStamp: startTimestamp,
        endTimeStamp: endTimestamp
      }),
    enabled: !!organisationId && !!userAccessToken && !!startTimestamp && !!endTimestamp,
    placeholderData: keepPreviousData
  });

  /* -------------------- STATE -------------------- */

  const loading =
    rolesQuery.isLoading ||
    organisationDetailsQuery.isLoading ||
    organisationActivityQuery.isLoading;

  const error =
    rolesQuery.error || organisationDetailsQuery.error || organisationActivityQuery.error;

  /* -------------------- RETURN -------------------- */

  return {
    organisationDetails: organisationDetailsQuery.data,
    organisationActivity: organisationActivityQuery.data,
    organisationAcitivityDateRange,
    roles: rolesQuery.data,
    orgDetailsLoading: loading,
    orgDetailsError: error
  };
};
